import {useWallet} from "@topo/wallet-adapter-react";
import {Menu, MenuItem, Theme, Tooltip} from "@mui/material";
import React, {JSX, useState} from "react";

type WalletMenuProps = {
  popoverAnchor: HTMLButtonElement | null;
  open: boolean;
  openedByHover: boolean;
  paperHoverProps: Pick<
    React.HTMLAttributes<HTMLElement>,
    "onMouseEnter" | "onMouseLeave"
  >;
  handlePopoverClose: () => void;
  handleNavigate?: () => void;
};

export default function WalletMenu({
  popoverAnchor,
  open,
  openedByHover,
  paperHoverProps,
  handlePopoverClose,
  handleNavigate,
}: WalletMenuProps): JSX.Element {
  const {account, disconnect} = useWallet();

  const onAccountOptionClicked = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    handleNavigate && handleNavigate();
    handlePopoverClose();
  };

  const handleLogout = () => {
    disconnect();
    handlePopoverClose();
  };

  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const copyAddress = async () => {
    if (account?.address) {
      await navigator.clipboard.writeText(account.address.toString());
      setTooltipOpen(true);
      setTimeout(() => {
        setTooltipOpen(false);
      }, 1500);
    }
  };

  const itemSx = {
    borderRadius: "6px",
    px: 1.5,
    py: 0.75,
    my: 0,
    fontSize: 14,
    lineHeight: 1.4,
    fontWeight: 400,
    color: (theme: Theme) => theme.palette.tokens.text.primary,
    "&:hover": {
      backgroundColor: (theme: Theme) =>
        theme.palette.tokens.background.secondary,
    },
  };

  return (
    <Menu
      id="wallet-menu"
      disableScrollLock
      autoFocus={!openedByHover}
      disableAutoFocus={openedByHover}
      disableEnforceFocus={openedByHover}
      disableRestoreFocus={openedByHover}
      sx={{pointerEvents: openedByHover ? "none" : "auto"}}
      anchorEl={popoverAnchor}
      open={open}
      onClose={handlePopoverClose}
      MenuListProps={{
        "aria-labelledby": "wallet-button",
        sx: {p: 0.5},
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          ...paperHoverProps,
          sx: (theme) => ({
            pointerEvents: "auto",
            width: popoverAnchor ? popoverAnchor.offsetWidth : "auto",
            minWidth: popoverAnchor ? popoverAnchor.offsetWidth : "auto",
            mt: 0,
            p: 0.5,
            borderRadius: "8px",
            border: `1px solid ${theme.palette.tokens.line.subtle}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.4)"
                : "0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)",
            backgroundColor: theme.palette.tokens.layer.modal,
          }),
        },
      }}
    >
      <Tooltip
        title="Copied!"
        placement="left"
        open={tooltipOpen}
        disableFocusListener
        disableHoverListener
        disableTouchListener
      >
        <MenuItem onClick={copyAddress} sx={itemSx}>
          Copy Address
        </MenuItem>
      </Tooltip>
      {!!handleNavigate && (
        <MenuItem onClick={onAccountOptionClicked} sx={itemSx}>
          Account
        </MenuItem>
      )}
      <MenuItem onClick={handleLogout} sx={itemSx}>
        Logout
      </MenuItem>
    </Menu>
  );
}
